export interface DashboardOverflowOffender {
  label: string;
  scrollHeight: number;
  clientHeight: number;
  overflowY: string;
  maxHeight: string;
}

export interface DashboardViewportMetrics {
  timestamp: string;
  viewport: { width: number; height: number };
  scrollHost: {
    clientHeight: number;
    scrollHeight: number;
    overflowY: string;
  } | null;
  main: {
    clientHeight: number;
    paddingTop: number;
    paddingBottom: number;
  } | null;
  fitHost: {
    clientHeight: number;
    offsetTop: number;
    top: number;
    bottom: number;
  };
  target: {
    scrollHeight: number;
    boundingHeight: number;
  };
  footer: { top: number } | null;
  availableHeight: number;
  naturalHeight: number;
  scale: number;
  scaledHeight: number;
  pageScroll: boolean;
  bottomClippedPx: number;
  offenders: DashboardOverflowOffender[];
}

const FIT_TARGET_SELECTOR = ".dashboard-fit-target";
const DEBUG_STORAGE_KEY = "dashboardLayoutDebug";

export function isDashboardLayoutDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(DEBUG_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function logDashboardViewportMetrics(metrics: DashboardViewportMetrics): void {
  if (!isDashboardLayoutDebugEnabled()) return;
  console.groupCollapsed(
    `[dashboard-fit] scale=${metrics.scale.toFixed(4)} available=${metrics.availableHeight}px natural=${metrics.naturalHeight}px`,
  );
  console.table(metrics);
  if (metrics.offenders.length > 0) {
    console.table(metrics.offenders);
  }
  console.groupEnd();
}

export function findDashboardOverflowOffenders(
  root: HTMLElement,
): DashboardOverflowOffender[] {
  const offenders: DashboardOverflowOffender[] = [];

  root.querySelectorAll<HTMLElement>("*").forEach((element) => {
    const styles = getComputedStyle(element);
    const overflowY = styles.overflowY;
    if (overflowY !== "auto" && overflowY !== "scroll") return;
    if (element.scrollHeight <= element.clientHeight + 1) return;

    const label =
      element.className?.toString().slice(0, 80) ||
      element.tagName.toLowerCase();

    offenders.push({
      label,
      scrollHeight: element.scrollHeight,
      clientHeight: element.clientHeight,
      overflowY,
      maxHeight: styles.maxHeight,
    });
  });

  return offenders.slice(0, 20);
}

export function measureAvailableDashboardHeight(host: HTMLElement): number {
  const scrollHost = host.closest(".main-layout-scroll") as HTMLElement | null;
  const main = host.closest("#main-content") as HTMLElement | null;
  const footer = document.querySelector(".minimal-footer") as HTMLElement | null;
  const hostRect = host.getBoundingClientRect();
  const footerTop = footer?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;

  if (scrollHost) {
    const scrollRect = scrollHost.getBoundingClientRect();
    const visualBottom = Math.min(scrollRect.bottom, footerTop);
    const mainPaddingBottom = main
      ? parseFloat(getComputedStyle(main).paddingBottom || "0")
      : 0;
    return Math.max(
      0,
      Math.floor(visualBottom - hostRect.top - mainPaddingBottom),
    );
  }

  if (main) {
    const mainRect = main.getBoundingClientRect();
    const mainStyles = getComputedStyle(main);
    const paddingBottom = parseFloat(mainStyles.paddingBottom || "0");
    const visualBottom = Math.min(mainRect.bottom, footerTop);
    return Math.max(
      0,
      Math.floor(visualBottom - hostRect.top - paddingBottom),
    );
  }

  return Math.max(0, host.clientHeight);
}

export function resetDashboardFitMeasurement(target: HTMLElement): void {
  target.style.setProperty("--dashboard-fit-scale", "1");
  target.style.removeProperty("transform");
  target.style.removeProperty("transformOrigin");
  if ("zoom" in target.style) {
    (target.style as CSSStyleDeclaration & { zoom: string }).zoom = "1";
  }
}

export function applyDashboardViewportScale(
  host: HTMLElement,
  scale: number,
): DashboardViewportMetrics | null {
  const target = host.querySelector(FIT_TARGET_SELECTOR) as HTMLElement | null;
  if (!target) return null;

  resetDashboardFitMeasurement(target);
  const naturalHeight = target.scrollHeight;
  const availableHeight = measureAvailableDashboardHeight(host);
  const clampedScale =
    naturalHeight > 0
      ? Math.max(0.62, Math.min(1, availableHeight / naturalHeight))
      : 1;

  const scaleValue = Number.isFinite(scale)
    ? Math.max(0.62, Math.min(1, scale))
    : clampedScale;

  target.style.setProperty("--dashboard-fit-scale", scaleValue.toFixed(4));

  if (typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("zoom", "1")) {
    (target.style as CSSStyleDeclaration & { zoom: string }).zoom =
      scaleValue.toFixed(4);
    target.style.removeProperty("transform");
    target.style.removeProperty("transformOrigin");
  } else {
    target.style.transform = `scale(${scaleValue})`;
    target.style.transformOrigin = "top center";
    if ("zoom" in target.style) {
      (target.style as CSSStyleDeclaration & { zoom: string }).zoom = "1";
    }
  }

  const scaledHeight = Math.ceil(naturalHeight * scaleValue);
  host.style.height = `${scaledHeight}px`;

  return buildDashboardViewportMetrics(host, target, {
    availableHeight,
    naturalHeight,
    scale: scaleValue,
    scaledHeight,
  });
}

function buildDashboardViewportMetrics(
  host: HTMLElement,
  target: HTMLElement,
  values: {
    availableHeight: number;
    naturalHeight: number;
    scale: number;
    scaledHeight: number;
  },
): DashboardViewportMetrics {
  const scrollHost = host.closest(".main-layout-scroll") as HTMLElement | null;
  const main = host.closest("#main-content") as HTMLElement | null;
  const hostRect = host.getBoundingClientRect();
  const scrollRect = scrollHost?.getBoundingClientRect();
  const footer = document.querySelector(".minimal-footer") as HTMLElement | null;
  const footerTop = footer?.getBoundingClientRect().top ?? null;
  const visualBottom = hostRect.top + values.scaledHeight;
  const pageBottom = Math.min(
    scrollRect?.bottom ?? window.innerHeight,
    footerTop ?? window.innerHeight,
  );
  const bottomClippedPx = Math.max(0, Math.ceil(visualBottom - pageBottom));

  return {
    timestamp: new Date().toISOString(),
    viewport: { width: window.innerWidth, height: window.innerHeight },
    scrollHost: scrollHost
      ? {
          clientHeight: scrollHost.clientHeight,
          scrollHeight: scrollHost.scrollHeight,
          overflowY: getComputedStyle(scrollHost).overflowY,
        }
      : null,
    main: main
      ? {
          clientHeight: main.clientHeight,
          paddingTop: parseFloat(getComputedStyle(main).paddingTop || "0"),
          paddingBottom: parseFloat(getComputedStyle(main).paddingBottom || "0"),
        }
      : null,
    fitHost: {
      clientHeight: host.clientHeight,
      offsetTop: host.offsetTop,
      top: hostRect.top,
      bottom: hostRect.bottom,
    },
    target: {
      scrollHeight: values.naturalHeight,
      boundingHeight: target.getBoundingClientRect().height,
    },
    footer: footerTop != null ? { top: footerTop } : null,
    availableHeight: values.availableHeight,
    naturalHeight: values.naturalHeight,
    scale: values.scale,
    scaledHeight: values.scaledHeight,
    pageScroll:
      (scrollHost?.scrollHeight ?? 0) > (scrollHost?.clientHeight ?? 0) + 1 ||
      document.documentElement.scrollHeight > window.innerHeight + 1,
    bottomClippedPx,
    offenders: findDashboardOverflowOffenders(target),
  };
}

export function measureDashboardViewport(
  host: HTMLElement,
): DashboardViewportMetrics | null {
  const target = host.querySelector(FIT_TARGET_SELECTOR) as HTMLElement | null;
  if (!target) return null;

  resetDashboardFitMeasurement(target);
  host.style.removeProperty("height");

  const naturalHeight = target.scrollHeight;
  const availableHeight = measureAvailableDashboardHeight(host);
  const scale =
    naturalHeight > 0
      ? Math.max(0.62, Math.min(1, availableHeight / naturalHeight))
      : 1;

  return applyDashboardViewportScale(host, scale);
}
